import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Chatbot from "@/components/Chatbot";
import SectionHeading from "@/components/SectionHeading";
import { ArrowUpRight } from "lucide-react";

const PORTRAIT = "/team/ceo-portrait.png";

const TENETS = [
  { n: "01", t: "Patience over volume.", d: "We accept fewer mandates each year than most desks close in a month. The work is slower; the outcomes are better." },
  { n: "02", t: "Provenance over inventory.", d: "Every residence we represent is sourced directly — from developer, from family, from a relationship maintained over years." },
  { n: "03", t: "The principal is the product.", d: "We do not sell properties. We earn the trust of principals — and over time, they invite us into more of their decisions." },
];

export default function CeoMessage() {
  return (
    <main data-testid="ceo-page" className="bg-[#f5f0e8] text-[#1c1c1c] grain">
      <Navbar solid />

      {/* Header */}
      <section className="pt-32 lg:pt-40 pb-16 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end">
          <div className="lg:col-span-7">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              A message<br />
              from the <em className="not-italic text-[#3d2b1f]">founder</em>.
            </h1>
            <span className="underline-label mt-7">Vision · 2026</span>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#3d2b1f] text-base leading-relaxed">
              The next decade in Dubai will look nothing like the last. Here is how AZEFAC will
              continue to serve its principals — and the few we hope will join them.
            </p>
          </div>
        </div>
      </section>

      {/* Letter + Portrait */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <div className="img-zoom aspect-[4/5]">
              <img src={PORTRAIT} alt="Founder of AZEFAC Properties" className="w-full h-full object-cover" />
            </div>
            <div className="mt-6">
              <div className="font-display text-2xl text-[#1c1c1c]">Azefac Dieudonne Chambang</div>
              <span className="underline-label mt-3">AZEFAC Properties · Dubai</span>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6 text-[#3d2b1f] text-lg leading-relaxed font-light">
            <p className="font-display text-3xl text-[#1c1c1c] leading-snug">
              When we founded AZEFAC, Dubai was a different city. The city has matured —
              and so has the kind of client it now attracts.
            </p>
            <p>
              The principal we serve today is rarely on their first home, and almost never on their
              first market. They have lived in London, New York, Singapore. They have engaged the best
              brokerages in each. And they have arrived in Dubai expecting more — not less — than the
              service they received elsewhere.
            </p>
            <p>
              Our work, then, is not to convince anyone of Dubai. The city does that on its own. Our
              work is to make the experience of buying here feel as considered as the one they would
              receive on Park Avenue, in Mayfair, or on the Côte d&apos;Azur. That means discretion,
              speed, and an unwavering preference for the off-market.
            </p>
            <p>
              In the next phase of AZEFAC, you will see us go deeper rather than broader. More direct
              developer allocations. More resident family-office structuring. More relationships that
              span generations, not transactions. We will continue to keep our principal count below
              ninety. We will continue to answer to no public shareholder.
            </p>
            <p>
              If this is the way you would like to be served, we look forward to meeting you.
            </p>
            <p className="font-display text-3xl text-[#1c1c1c] mt-10 italic leading-tight">
              — Azefac Dieudonne Chambang
            </p>
            <p className="font-display text-xl text-[#3d2b1f] italic mt-1">
              Founder of AZEFAC Properties - Dubai
            </p>
          </div>
        </div>
      </section>

      {/* The Three Tenets */}
      <section className="bg-[#ede5d5] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <SectionHeading title={<>Three tenets,<br />for the <em className="not-italic text-[#3d2b1f]">next decade</em>.</>} subtitle="The Vision · 2026 — 2036" />
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-10">
            {TENETS.map((t) => (
              <div key={t.n} data-testid={`tenet-${t.n}`} className="border-t border-[#1c1c1c]/15 pt-6">
                <div className="font-display text-4xl text-[#c5a059]">{t.n}</div>
                <h3 className="font-display text-2xl mt-3 leading-tight">{t.t}</h3>
                <p className="mt-4 text-sm text-[#3d2b1f] leading-relaxed">{t.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-36">
        <div className="border-t border-[#1c1c1c]/10 pt-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <h2 className="font-display text-3xl lg:text-5xl text-[#1c1c1c] leading-tight max-w-2xl">
            Read the story of how<br />
            AZEFAC was brought to life.
          </h2>
          <Link
            to="/about"
            data-testid="ceo-cta-about"
            className="inline-flex items-center gap-3 px-7 py-4 bg-[#c5a059] text-[#1c1c1c] uppercase text-[11px] tracking-[0.32em] hover:bg-[#1c1c1c] hover:text-[#f5f0e8] transition-colors duration-500"
          >
            Our Story <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </main>
  );
}
